import * as React from 'react';
import {
  Checkbox,
  IconButtonProps,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Box,
} from '@mui/material';
import ExtractionBatchRow from './ExtractionBatchRow';
import { IExtractionBatch } from '../../types/my-types';

import { useNavigate, useLocation } from 'react-router';

import { standardTableStyles } from '../common/tableStyles';

interface ExtractionBatchListProps {
  extractionBatches: IExtractionBatch[];
  count: number;
  page: number;
  perPage: number;
  nextPage: any;
  previousPage: any;
  isFetching: boolean;
  isPending: boolean;
  refetch: () => void;
}

export interface IEBTrainingSetLabels {
  [key: string]: string;
}

function ExtractionBatchList({
  extractionBatches: currentBatches,
  count,
  page,
  perPage,
  nextPage,
  previousPage,
  isFetching,
  isPending: isLoading,
  refetch,
}: ExtractionBatchListProps) {
  const navigate = useNavigate();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const selectedIds = searchParams.get('selectedIds')?.split(',') || [];
  const visibleIds = currentBatches.map((eb) => eb.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const handleToggleSelectAll = () => {
    const params = new URLSearchParams(search);
    if (allVisibleSelected) {
      const remaining = selectedIds.filter((id) => !visibleIds.includes(id));
      if (remaining.length === 0) {
        params.delete('selectedIds');
      } else {
        params.set('selectedIds', remaining.join(','));
      }
    } else {
      const merged = Array.from(new Set([...selectedIds, ...visibleIds]));
      params.set('selectedIds', merged.join(','));
    }
    navigate({ search: params.toString() });
  };

  return (
    <>
      <Box id="extraction-batch-list" sx={standardTableStyles.containerSx}>
        <Table sx={standardTableStyles.tableSx}>
          <TableHead sx={standardTableStyles.tableHeadSx}>
            <TableRow>
              <TableCell sx={{ width: '40px', px: '4px !important', py: '0 !important' }}>
                <Checkbox
                  size="small"
                  inputProps={
                    {
                      'aria-label': 'select all extraction batches',
                      'data-cy': 'checkbox-eb-list-all-rows',
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  checked={allVisibleSelected}
                  onChange={handleToggleSelectAll}
                  disabled={visibleIds.length === 0}
                  sx={{ color: '#65BBA9', '&.Mui-checked': { color: '#65BBA9' } }}
                />
              </TableCell>
              <TableCell>Extraction Batch</TableCell>
              <TableCell align="center">Documents</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="center">Updated</TableCell>
              <TableCell align="center">Sent?</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            data-cy="extraction-batch-table-body"
            sx={standardTableStyles.tableBodySx}
          >
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ width: '40px', px: '4px !important' }} />
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Skeleton animation="wave" variant="circular" width={17} height={17} />
                        <Skeleton animation="wave" variant="rounded" height={14} width={`${35 + (i * 21) % 50}%`} sx={{ borderRadius: '4px' }} />
                      </Box>
                      <Skeleton animation="wave" variant="rounded" height={12} width={80} sx={{ borderRadius: '4px', mt: 1, ml: '29px' }} />
                    </TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={24} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={72} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={72} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="circular" width={18} height={18} sx={{ mx: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton animation="wave" variant="rounded" height={14} width={24} sx={{ borderRadius: '4px', ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))
              : null}

            {!isLoading && currentBatches.length > 0
              ? currentBatches?.map((eb: any) => <ExtractionBatchRow key={eb.id} eb={eb} />)
              : null}

            {currentBatches.length === 0 && !isFetching && !isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    No extraction batches found
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {count > 0 && <TablePagination
          rowsPerPageOptions={[10, 20, 25, 50]}
          component="div"
          count={count}
          rowsPerPage={perPage}
          page={page}
          backIconButtonProps={
            {
              'aria-label': 'Previous Page',
              'data-cy': 'btn-prev-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          nextIconButtonProps={
            {
              'aria-label': 'Next Page',
              'data-cy': 'btn-next-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          onPageChange={(e, p) => {
            e?.preventDefault();
            if (p > page) {
              nextPage();
            } else {
              previousPage();
            }

            searchParams.set('page', p.toString());
            navigate({
              search: searchParams.toString(),
            });
          }}
          onRowsPerPageChange={(e: any) => {
            searchParams.set('perPage', e.target.value);
            navigate({
              search: searchParams.toString(),
            });
          }}
        />}
      </Box>
    </>
  );
}
export default ExtractionBatchList;
